import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import AdvanceDataTable from '@/components/core/advance-data-table/advance-data-table';
import { PageHeader } from '@/components/core';
import { HRM_PERMISSIONS } from '@/constant/hr/permissions';
import { useHasPermission } from '@/hooks/use-has-permission';
import { useServerTable } from '@/hooks/use-server-table';
import { mergeWhere } from '@/lib/table-query';
import { Input } from '@/components/ui-kit/input';
import { createDesignationTableColumns } from '../../components/designation-table-columns/designation-table-columns';
import { DesignationFormDialog } from '../../components/designation-form-dialog/designation-form-dialog';
import { useGetDesignations } from '../../hooks/use-designations';
import { DEFAULT_DESIGNATIONS_ORDER } from '../../graphql/queries';
import { Designation, DesignationsPagingResult } from '../../types/designation.types';

export const DesignationManagerPage = () => {
  const { t } = useTranslation();
  const canCreate = useHasPermission(HRM_PERMISSIONS.SETTINGS.DESIGNATIONS_CREATE);
  const canUpdate = useHasPermission(HRM_PERMISSIONS.SETTINGS.DESIGNATIONS_UPDATE);

  const [keyword, setKeyword] = useState('');
  const [debouncedKeyword, setDebouncedKeyword] = useState('');
  const { pagination, resetPage, order, columnWhere, tableProps } = useServerTable({
    pageSize: 15,
    defaultOrder: DEFAULT_DESIGNATIONS_ORDER,
  });
  const [formState, setFormState] = useState<{ open: boolean; designation: Designation | null }>({
    open: false,
    designation: null,
  });

  useEffect(() => {
    const timeout = setTimeout(() => setDebouncedKeyword(keyword.trim()), 600);
    return () => clearTimeout(timeout);
  }, [keyword]);

  useEffect(() => {
    resetPage();
  }, [debouncedKeyword, resetPage]);

  const filter = debouncedKeyword ? { Title: { $regex: debouncedKeyword, $options: 'i' } } : {};

  const { data, isLoading, error } = useGetDesignations({
    pageNo: pagination.pageIndex + 1,
    pageSize: pagination.pageSize,
    filter: mergeWhere(filter, columnWhere),
    order,
  });
  const result = (data as { Designations?: DesignationsPagingResult })?.Designations;
  const items = result?.items ?? [];
  const totalCount = result?.totalCount ?? 0;

  const columns = createDesignationTableColumns({
    t,
    onEdit: (designation) => setFormState({ open: true, designation }),
    canUpdate,
  });

  return (
    <div className="flex w-full flex-col gap-4">
      <PageHeader
        title={t('DESIGNATIONS')}
        actions={
          canCreate
            ? [
                {
                  label: t('CREATE_DESIGNATION'),
                  icon: <Plus className="size-5" />,
                  onClick: () => setFormState({ open: true, designation: null }),
                },
              ]
            : []
        }
      />

      <Input
        placeholder={t('SEARCH')}
        value={keyword}
        onChange={(e) => setKeyword(e.target.value)}
        className="max-w-xs shrink-0"
      />

      <AdvanceDataTable
        {...tableProps}
        data={items}
        columns={columns}
        isLoading={isLoading}
        error={error instanceof Error ? error : null}
        pagination={{
          pageIndex: pagination.pageIndex,
          pageSize: pagination.pageSize,
          totalCount,
        }}
        isExpandRowContent={false}
        mobileView="cards"
      />

      <DesignationFormDialog
        open={formState.open}
        onOpenChange={(open) =>
          setFormState((prev) => ({ open, designation: open ? prev.designation : null }))
        }
        designation={formState.designation}
      />
    </div>
  );
};
